import { ShieldCheck, HeartHandshake, PackageCheck, FlaskConical, AlertTriangle } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Product Design",
    desc: "Soft-touch materials — BPA free and non-toxic — with a protected internal chamber and gentle front ventilation holes.",
  },
  {
    icon: HeartHandshake,
    title: "Comfort System",
    desc: "The cotton insert remains contained inside the chamber while Mi Binky is in use. Nothing enters the mouth but the pacifier itself.",
  },
  {
    icon: PackageCheck,
    title: "Accessories",
    desc: "Sterile cotton inserts, 15 ml comfort bottles and precision tweezers — every accessory designed to work together as one system.",
  },
  {
    icon: FlaskConical,
    title: "Conformity Testing",
    desc: "The Mi Binky design is undergoing independent conformity testing. Final instructions and claims will reflect the completed testing.",
  },
];

const RULES = [
  "Adult use only when preparing the comfort chamber",
  "Always supervise babies and toddlers while using any pacifier",
  "Use only compatible BINKY Comfort accessories",
  "Replace worn or damaged products immediately",
];

export const Safety = () => (
  <section id="safety" data-testid="safety-section" className="relative overflow-hidden bg-binky-navy py-24 text-white sm:py-32">
    <div className="pointer-events-none absolute -left-32 top-0 h-[420px] w-[420px] rounded-full bg-binky-pink/20 blur-[120px]" />
    <div className="pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-binky-blue/40 blur-[120px]" />

    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow className="text-binky-pink">Safety & Product Development</Eyebrow>
        <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
          Designed carefully. <span className="text-binky-pink">Tested carefully.</span>
        </h2>
        <p className="mt-4 text-base leading-relaxed text-white/75 md:text-lg">
          Mi Binky is being developed with the care a baby product deserves. The pacifier design is
          undergoing independent conformity testing, and final product instructions and claims will
          reflect the completed testing — nothing less.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {PILLARS.map((p, i) => {
          const Icon = p.icon;
          return (
            <Reveal
              key={p.title}
              delay={i * 0.08}
              className="rounded-[28px] border border-white/10 bg-white/5 p-7 backdrop-blur-sm transition-colors duration-500 hover:bg-white/10"
              data-testid={`safety-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-binky-pink/20 text-binky-pink">
                <Icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-display text-lg font-bold">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/70">{p.desc}</p>
            </Reveal>
          );
        })}
      </div>

      <Reveal className="mt-14" data-testid="safety-guidance">
        <div className="rounded-[28px] border border-white/10 bg-white/5 p-8 backdrop-blur-sm sm:p-10">
          <h3 className="font-display text-xl font-bold">Using Mi Binky safely</h3>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {RULES.map((r) => (
              <div key={r} className="flex items-start gap-3 text-sm font-semibold text-white/85">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-binky-green/20 text-binky-green">
                  <ShieldCheck className="h-3.5 w-3.5" />
                </span>
                {r}
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-start gap-3 rounded-2xl border border-binky-pink/30 bg-binky-pink/10 px-5 py-4" data-testid="safety-disclaimer">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-binky-pink" />
            <p className="text-xs leading-relaxed text-white/75 sm:text-sm">
              Mi Binky is currently in pre-launch. Independent conformity testing is in progress and
              no safety certification is claimed at this stage. Final product instructions and claims
              will be published once testing is complete.
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
